import { useQuery } from "@tanstack/react-query";
import {
  getAdminDashboard,
  getAdminVentures,
  getAdminVenture,
  getAdminVentureDueDiligence,
  getAdminDataCollection,
  getAdminImpact,
  getAdminPortfolio,
  getAdminReports,
  getAdminSettings,
  getAdminTraining,
} from "../api";
import { useAuth } from "../context/authContext";

export function useAdminDashboardHook({ token, userId, enabled = true }) {
  return useQuery({
    queryKey: ["adminDashboard", userId],
    enabled: enabled && !!token && !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getAdminDashboard({ token });
      if (!result.ok) {
        throw new Error(result.error || "Unable to load admin dashboard data.");
      }
      return result.data;
    },
  });
}

export function useAdminDataCollectionHook({ token, userId, enabled = true }) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;
  const resolvedUserId = userId ?? auth.user?.id;

  return useQuery({
    queryKey: ["adminDataCollection", resolvedUserId],
    enabled: enabled && !!resolvedToken && !!resolvedUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getAdminDataCollection({ token: resolvedToken });

      if (!result.ok) {
        throw new Error(result.error || "Unable to fetch data collection data");
      }

      return result.dataCollection;
    },
  });
}

export function useAdminVenturesHook({ token, userId, enabled = true }) {
  return useQuery({
    queryKey: ["adminVentures", userId],
    enabled: enabled && !!token && !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getAdminVentures({ token });
      if (!result.ok) {
        throw new Error(result.error || "Unable to load ventures data.");
      }
      return result.ventures;
    },
  });
}

export function useAdminVentureHook({ token, ventureId, enabled = true }) {
  return useQuery({
    queryKey: ["adminVenture", ventureId],
    enabled: enabled && !!token && !!ventureId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getAdminVenture({ token, ventureId });
      if (!result.ok) {
        throw new Error(result.error || "Unable to load venture details.");
      }
      return result.venture;
    },
  });
}

export function useAdminVentureDueDiligenceHook({
  token,
  ventureId,
  enabled = true,
}) {
  return useQuery({
    queryKey: ["adminVentureDueDiligence", ventureId],
    enabled: enabled && !!token && !!ventureId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getAdminVentureDueDiligence({ token, ventureId });
      if (!result.ok) {
        throw new Error(
          result.error || "Unable to load venture due diligence score.",
        );
      }
      return result.dueDiligence;
    },
  });
}

export function useAdminImpactHook({ token, userId, enabled = true }) {
  return useQuery({
    queryKey: ["adminImpact", userId],
    enabled: enabled && !!token && !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getAdminImpact({ token });
      if (!result.ok) {
        throw new Error(result.error || "Unable to load impact data.");
      }
      return result.impact;
    },
  });
}

export function useAdminPortfolioHook({ token, userId, enabled = true }) {
  return useQuery({
    queryKey: ["adminPortfolio", userId],
    enabled: enabled && !!token && !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getAdminPortfolio({ token });
      if (!result.ok) {
        throw new Error(result.error || "Unable to load portfolio data.");
      }
      return result.portfolio;
    },
  });
}

export function useAdminReportsHook({ token, userId, enabled = true }) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;
  const resolvedUserId = userId ?? auth.user?.id;

  return useQuery({
    queryKey: ["adminReports", resolvedUserId],
    enabled: enabled && !!resolvedToken && !!resolvedUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getAdminReports({ token: resolvedToken });

      if (!result.ok) {
        throw new Error(result.error || "Unable to fetch reports data");
      }

      return result.reports;
    },
  });
}

export function useAdminSettingsHook({ token, userId, enabled = true }) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;
  const resolvedUserId = userId ?? auth.user?.id;

  return useQuery({
    queryKey: ["adminSettings", resolvedUserId],
    enabled: enabled && !!resolvedToken && !!resolvedUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getAdminSettings({ token: resolvedToken });

      if (!result.ok) {
        throw new Error(result.error || "Unable to fetch settings data");
      }

      return result.settings;
    },
  });
}

export function useAdminTrainingHook({ token, userId, enabled = true }) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;
  const resolvedUserId = userId ?? auth.user?.id;

  return useQuery({
    queryKey: ["adminTraining", resolvedUserId],
    enabled: enabled && !!resolvedToken && !!resolvedUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getAdminTraining({ token: resolvedToken });

      if (!result.ok) {
        throw new Error(result.error || "Unable to fetch training data");
      }

      return result.training;
    },
  });
}
